import i18n from '@/utils/i18n';
import { ACCOUNT_LIST_PAGE_SIZE } from '@/experiences/sales-portal/domain/accounts/screens/account-list/blocs/loaders/use-account-list-table-paginated-loader';
import {
  buildNearbyVariables,
  NearbyBusiness,
  NearbyBusinessFilters,
} from '@/experiences/sales-portal/domain/prospects/blocs/loaders/use-prospect-finder-nearby-businesses-loader';
import { captureException } from '@/utils/sentry';
import {
  ChatStatus,
  CreationSourceFeatures,
  SalesPortalProspectFinderNearbyBusinessesQuery,
  SalesPortalProspectFinderNearbyBusinessesQueryVariables,
} from '@/types';
import { getDeviceTimezone } from '@/utils/date';
import { partnerSupplier } from '@/utils/environment';
import { SalesPortalAccountListDataPaginatedDocument } from '@/experiences/sales-portal/domain/accounts/screens/account-list/blocs/documents/sales-portal-account-list-data-paginated.generated';
import { SalesPortalAccountListDocument } from '@/experiences/sales-portal/blocs/documents/sales-portal-account-list.generated';
import { SalesPortalAnalyticEvent } from '@/experiences/sales-portal/analytics/fennel/registry';
import { SalesPortalProspectFinderNearbyBusinessesDocument } from '@/experiences/sales-portal/domain/prospects/blocs/documents/sales-portal-prospect-finder-nearby-businesses.generated';
import { SalesPortalRepViewProspectsDocument } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/documents/sales-portal-rep-view-prospects.generated';
import { track } from '@/experiences/sales-portal/analytics/fennel/hook';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSalesPortalProspectFinderCreateAccountMutation } from '@/experiences/sales-portal/domain/prospects/blocs/documents/sales-portal-prospect-finder-create-account.generated';

/**
 * Claims an available lead via `supplierCreateRestaurant`, then flips the
 * matching nearby-businesses cache row to existing so the row, pin, and card
 * update without a refetch. Also refetches the Accounts and Prospects lists
 * and the Sales Hub account selector so the new account appears there
 * without a reload. One-way: no unclaim.
 *
 * @param supplierUUID - Supplier doing the claim; written to both the mutation
 * variables and the cache lookup variables.
 * @param filters - Current nearby filters; read via a ref at update-callback
 * time so a filter change mid-flight writes to the cache entry the rep is
 * looking at. `isCurrentRestaurant === false` drops the row from the list
 * (Prospects filter); otherwise the row's `restaurant_uuid` is flipped in
 * place.
 * @returns `createAccount(business, source)` — fires the mutation; no-ops if
 * the business is already in flight. `source` records where the claim began.
 * `claimingYelpIds` — every yelpBusinessId in flight (drives the map card
 * loading state). `listClaimingYelpIds` — the subset claimed from the list
 * row; drives the row loading state so a card-initiated claim loads only the
 * card.
 */
export type ClaimSource = 'list' | 'card';

export const useCreateProspectAccountMutator = (
  supplierUUID: string,
  filters: NearbyBusinessFilters,
) => {
  const { showErrorToast, showToast } = useExperienceToasts();
  const [createAccountMutation] =
    useSalesPortalProspectFinderCreateAccountMutation();
  // Read at mutation-resolve time so a filter change mid-flight writes to
  // the cache entry the rep is currently looking at.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  // Ref for the synchronous double-click guard; state to re-render the
  // loading button. `listClaimingYelpIds` is the subset whose claim began on
  // the list row — a list claim loads both the row and the map card, while a
  // claim from the map card loads only the card.
  const claimingRef = useRef<Set<string>>(new Set());
  const [claimingYelpIds, setClaimingYelpIds] = useState(claimingRef.current);
  const listClaimingRef = useRef<Set<string>>(new Set());
  const [listClaimingYelpIds, setListClaimingYelpIds] = useState(
    listClaimingRef.current,
  );
  const updateClaiming = useCallback((next: Set<string>) => {
    claimingRef.current = next;
    setClaimingYelpIds(next);
  }, []);
  const updateListClaiming = useCallback((next: Set<string>) => {
    listClaimingRef.current = next;
    setListClaimingYelpIds(next);
  }, []);

  const createAccount = useCallback(
    async (business: NearbyBusiness, source: ClaimSource = 'list') => {
      if (claimingRef.current.has(business.yelpBusinessId)) {
        return;
      }
      track(SalesPortalAnalyticEvent.PROSPECT_FINDER_ACCOUNT_CREATION_CLICK, {
        source,
        yelpBusinessId: business.yelpBusinessId,
      });
      const next = new Set(claimingRef.current);
      next.add(business.yelpBusinessId);
      updateClaiming(next);
      if (source === 'list') {
        const nextList = new Set(listClaimingRef.current);
        nextList.add(business.yelpBusinessId);
        updateListClaiming(nextList);
      }
      try {
        const { data } = await createAccountMutation({
          refetchQueries: [
            {
              query: SalesPortalAccountListDataPaginatedDocument,
              variables: {
                businessOrganizationUUID:
                  partnerSupplier.businessOrganizationUUID,
                pageSize: ACCOUNT_LIST_PAGE_SIZE,
                userTimezone: getDeviceTimezone(),
              },
            },
            {
              query: SalesPortalRepViewProspectsDocument,
              variables: {
                businessOrganizationUUID:
                  partnerSupplier.businessOrganizationUUID,
                userTimezone: getDeviceTimezone(),
              },
            },
            {
              query: SalesPortalAccountListDocument,
              variables: {
                businessOrganizationUUID:
                  partnerSupplier.businessOrganizationUUID,
              },
            },
          ],
          update: (cache, { data: result }) => {
            const restaurant = result?.supplierCreateRestaurant.business;
            if (!restaurant) {
              return;
            }
            const [chat] = restaurant.restaurant_chats;
            const claimedFields = {
              restaurant_uuid: restaurant.uuid,
              status: chat?.status as ChatStatus | undefined,
              supplier_uuid: supplierUUID,
            };
            const currentFilters = filtersRef.current;
            // Same builder the query uses, so we write to the exact cache entry.
            const nearbyVariables = buildNearbyVariables(
              supplierUUID,
              currentFilters,
            );
            // On the Prospects tab the claimed row no longer belongs; otherwise flip it to Existing.
            const dropFromList = currentFilters.isCurrentRestaurant === false;
            cache.updateQuery<
              SalesPortalProspectFinderNearbyBusinessesQuery,
              SalesPortalProspectFinderNearbyBusinessesQueryVariables
            >(
              {
                query: SalesPortalProspectFinderNearbyBusinessesDocument,
                variables: nearbyVariables,
              },
              (cached) =>
                cached
                  ? {
                      ...cached,
                      getNearbyBusinessesV2: dropFromList
                        ? cached.getNearbyBusinessesV2.filter(
                            (row) =>
                              row.yelp_business_id !== business.yelpBusinessId,
                          )
                        : cached.getNearbyBusinessesV2.map((row) =>
                            row.yelp_business_id === business.yelpBusinessId
                              ? { ...row, ...claimedFields }
                              : row,
                          ),
                    }
                  : cached,
            );
          },
          variables: {
            address: business.displayAddress,
            creationSourceFeature: CreationSourceFeatures.LeadGenerator,
            metadata: {
              cuisine: business.categories?.[0],
              display_phone_number: business.phoneNumber ?? '',
              yelp_business_id: business.yelpBusinessId,
            },
            name: business.businessName,
            supplierUUID,
          },
        });
        if (data?.supplierCreateRestaurant.business?.uuid) {
          track(
            SalesPortalAnalyticEvent.PROSPECT_FINDER_PROSPECT_ACCOUNT_CREATED,
            {
              restaurantUUID: data.supplierCreateRestaurant.business.uuid,
              yelpBusinessId: business.yelpBusinessId,
            },
          );
          showToast({
            title: i18n.t('sales_portal.prospect_finder.claim.success', {
              name: business.businessName,
            }),
            variant: 'success',
          });
        } else {
          captureException(
            new Error('supplierCreateRestaurant returned no business uuid'),
          );
          showErrorToast(i18n.t('sales_portal.prospect_finder.claim.error'));
        }
      } catch (error) {
        captureException(error);
        showErrorToast(i18n.t('sales_portal.prospect_finder.claim.error'));
      } finally {
        const after = new Set(claimingRef.current);
        after.delete(business.yelpBusinessId);
        updateClaiming(after);
        if (listClaimingRef.current.has(business.yelpBusinessId)) {
          const afterList = new Set(listClaimingRef.current);
          afterList.delete(business.yelpBusinessId);
          updateListClaiming(afterList);
        }
      }
    },
    [
      createAccountMutation,
      showErrorToast,
      showToast,
      supplierUUID,
      updateClaiming,
      updateListClaiming,
    ],
  );

  return useMemo(
    () => ({ claimingYelpIds, createAccount, listClaimingYelpIds }),
    [claimingYelpIds, createAccount, listClaimingYelpIds],
  );
};
